import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { T } from "../../../lib/i18n";

/**
 * 设计部通用的「二次确认」弹窗（2026-10-26）。
 *
 * 【为什么要有这个组件】
 * 结项 / 撤销结项原先用原生 `window.prompt` / `window.confirm` 收确认与原因，
 * 有两个实际问题：
 *   ① 不支持原生弹窗的宿主（如 ZCode 内置浏览器 IAB）会直接抛
 *      `prompt() is not supported.`，页面顶上糊一层红色 Uncaught runtime error，
 *      用户完全不知道刚才那一步到底有没有生效；
 *   ② 原生弹窗只能塞一段纯文本，没法表达「这一步不可逆、会停掉哪些催办、
 *      和另一个终态（移交施工）有什么区别」这类必须讲清楚的信息，
 *      结果就是终态操作被当成普通状态切换随手点掉。
 *
 * 【设计口径】
 * - 复用 Radix 之外的既有做法：本项目弹窗统一用 `createPortal` + 固定遮罩
 *   （同 ProjectDrawer 的终态确认、HandoffDialog），不引入新的弹窗体系。
 * - `tone="danger"` 走红色语义（撤销、作废这类不可逆动作），`tone="default"`
 *   走品牌绿（正常推进）。危险操作用红色是项目基线要求，不能用品牌蓝。
 * - 原因输入框可选：`reason` 相关属性都不传则不渲染输入框。
 * - 键盘：Esc 关闭、打开时自动聚焦（有输入框聚焦输入框，否则聚焦确认按钮），
 *   保证不用鼠标也能完成或取消。
 *
 * 【为什么不做成全局通用组件】
 * 本项目已有 `components/ui/alert-dialog.jsx`（Radix）。这里没直接用它，
 * 是因为设计部这一层（ProjectDrawer / CompletedList / HandoffDialog）现有弹窗
 * 全部是 portal + 手写遮罩的同一套写法，混用两套会让遮罩层级（z-index）、
 * 间距和按钮尺寸在同一屏里不一致。后续若要全站统一，再把这个组件替换成
 * Radix 封装即可，调用方签名不变。
 */
export const DesignConfirmDialog = ({
  title,                 // 标题（必填）
  subtitle,              // 标题下的一行副标题，通常是项目地址
  message,               // 正文说明：这一步会发生什么
  warning,               // 需要强调的后果（琥珀底），可为空
  tone = "default",      // "default" | "danger"
  confirmText = T("确认"),
  cancelText = T("取消"),
  busy = false,
  withReason = false,    // 是否显示原因输入框
  reasonLabel,
  reasonPlaceholder,
  reasonRequired = false,
  reasonDefault = "",
  onCancel,
  onConfirm,             // (reason) => void
  testId = "dv3-confirm",
}) => {
  const [reason, setReason] = useState(reasonDefault);
  const inputRef = useRef(null);
  const btnRef = useRef(null);
  const danger = tone === "danger";

  // 打开即聚焦：有输入框先给输入框，否则给确认按钮。
  // 原生 prompt 有这个行为，换成自建弹窗后必须补上，否则键盘用户要多按一次 Tab。
  useEffect(() => {
    const el = withReason ? inputRef.current : btnRef.current;
    el?.focus();
  }, [withReason]);

  // Esc 关闭：与原生弹窗的行为对齐（原生 confirm/prompt 都支持 Esc 取消）。
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  const confirmDisabled = busy || (reasonRequired && !reason.trim());

  return createPortal(
    <div
      className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4"
      onClick={() => { if (!busy) onCancel?.(); }}
      data-testid={`${testId}-overlay`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className={`h-5 w-5 mt-0.5 shrink-0 ${danger ? "text-red-600" : "text-emerald-600"}`} />
          <div className="min-w-0">
            <h3 id={`${testId}-title`} className="font-bold text-slate-900 text-sm">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate" data-testid={`${testId}-subtitle`}>{subtitle}</p>}
          </div>
        </div>
        {message && <p className="text-xs text-slate-600 leading-5" data-testid={`${testId}-message`}>{message}</p>}
        {warning && (
          <div className={`rounded-lg px-3 py-2 text-[11px] leading-5 border ${danger ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid={`${testId}-warning`}>
            {warning}
          </div>
        )}
        {withReason && (
          <label className="block text-xs text-slate-600 space-y-1">
            <span className="font-semibold">
              {reasonLabel || T("原因")}
              {!reasonRequired && <span className="font-normal text-slate-400">{T("（可选）")}</span>}
            </span>
            <textarea
              ref={inputRef}
              data-testid={`${testId}-reason`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder || ""}
              rows={3}
              maxLength={300}
              disabled={busy}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            />
            <span className="block text-[10px] text-slate-400 text-right">{reason.length}/300</span>
          </label>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="outline" data-testid={`${testId}-cancel`} onClick={onCancel} disabled={busy}>
            {cancelText}
          </Button>
          <Button
            ref={btnRef}
            size="sm"
            data-testid={`${testId}-confirm`}
            onClick={() => onConfirm?.(reason.trim())}
            disabled={confirmDisabled}
            className={danger ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmText}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
